/** @jsxImportSource @emotion/react */
import { Header, useSession } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Input,
  Modal,
  Typography,
  UtilityButton,
  useTheme,
  Snackbar,
  Checkbox,
} from '@quickbit/qb-design-system';
import { useCallback, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAsync, useAsyncFn, useMount } from 'react-use';
import { QBLoader } from 'components';
import {
  createSettlementReportAdjustment,
  deleteSettlementReport,
  deleteSettlementReportAdjustment,
  emailSettlementReport,
  getSettlementReport,
  markProcessed,
  updateRefundDebtAdjustment,
  updateSettlementReportAdjustment,
} from 'tabs/Accounting/api';
import { SettlementReportAdjustment } from 'tabs/Accounting/types';
import { getKYB } from 'tabs/Merchants/api';
import { AccountingReporting, Adjustments, Payments } from './components';
import { useGeneratePDF } from './hooks';
import { useGetSettlementReportSheet } from './utils';

export const Details = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { spacing, palette } = useTheme();
  const session = useSession();
  const {
    generatePDF,
    loading: pdfLoading,
    error: pdfError,
    setError: setPDFError,
  } = useGeneratePDF();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMarkAsProcessedModal, setShowMarkAsProcessedModal] =
    useState(false);
  const [sendReportAsEmail, setSendReportAsEmail] = useState(true);
  const [refundDebtAdjustment, setRefundDebtAdjustment] = useState<
    number | null
  >(null);

  const { download, isLoading } = useGetSettlementReportSheet(reportId);

  const canDeleteReport =
    session.authenticated &&
    session.permissions.includes('edit_settlement_report');

  if (!reportId) {
    void navigate('/accounting/reports');
    throw new Error('No report id');
  }

  const [{ value, loading: refreshLoading }, refresh] = useAsyncFn(async () => {
    const report = await getSettlementReport(reportId);
    if (report.refundDebt !== null) {
      setRefundDebtAdjustment(report.refundDebt);
    }
    return report;
  }, [reportId]);

  const [{ loading: addLoading }, addNewAdjustment] = useAsyncFn(async () => {
    await createSettlementReportAdjustment(reportId, {
      amount: 0,
      label: 'New adjustment field',
    });
  }, [reportId]);

  const [{ loading: deleteLoading }, deleteAdjustment] = useAsyncFn(
    async (adjustmentId: string) => {
      await deleteSettlementReportAdjustment(reportId, adjustmentId);
    },
    [reportId]
  );

  const { value: merchantKyb, loading: merchantKybLoading } =
    useAsync(async () => {
      if (value?.merchant) {
        return getKYB(value.merchant.uuid);
      }
    }, [value]);

  const [, updateAdjustment] = useAsyncFn(
    async (updatedField: SettlementReportAdjustment) => {
      await updateSettlementReportAdjustment(reportId, updatedField);
    }
  );

  const [, updateRefundAdjustment] = useAsyncFn(async (amount: number) => {
    await updateRefundDebtAdjustment(reportId, amount);
  });

  const [
    { value: sendReportEmailValue, loading: sendReportEmailLoading },
    sendReportEmail,
  ] = useAsyncFn(async () => {
    if (value && sendReportAsEmail && merchantKyb?.settlementReportEmails) {
      const pdf = await generatePDF({
        settlementData: value,
        merchantKyb,
        isPreview: false,
      });
      if (pdf) {
        return await emailSettlementReport({
          reportId,
          settlementReportPdf: pdf.output('datauristring').split(',')[1],
        });
      }
    } else {
      void Promise.resolve({}); // This is to avoid the warning of not returning a promise
    }
  }, [value, reportId, sendReportAsEmail, merchantKyb]);

  const [{ loading: markProcessedLoading }, markAsProcessed] =
    useAsyncFn(async () => {
      await markProcessed({ reportId });
      await sendReportEmail();
      await refresh();
      setShowMarkAsProcessedModal(false);
    }, [reportId, refresh, sendReportAsEmail, merchantKyb]);

  useMount(() => {
    void refresh();
  });

  const [{ loading: deleteReportLoading }, deleteReport] =
    useAsyncFn(async () => {
      await deleteSettlementReport(reportId);
    }, [reportId]);

  const downloadPdfHandler = useCallback(async () => {
    if (merchantKyb && value) {
      const pdf = await generatePDF({
        settlementData: value,
        merchantKyb,
      });
      //This will download the pdf
      if (pdf) {
        pdf.save(
          `${value.merchant.name.replaceAll(
            ' ',
            '_'
          )}-${value.startTime.toLocaleDateString()}-${value.endTime.toLocaleDateString()}.pdf`
        );
      }
    }
  }, [value, generatePDF, merchantKyb]);

  if (!value) return null;

  return (
    <>
      <Header>
        {' '}
        <div
          css={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div css={{ display: 'flex', gap: spacing.xxs }}>
            <Typography variant="subtitle">Report details</Typography>
          </div>
          <div css={{ gap: spacing.xxxs, display: 'flex' }}>
            {!value.processed && canDeleteReport && (
              <UtilityButton
                text="Delete report"
                variant="secondary"
                onClick={() => setShowDeleteModal(true)}
              />
            )}
            <UtilityButton
              text="Download PDF"
              onClick={downloadPdfHandler}
              loading={pdfLoading}
            />
            <UtilityButton
              text="Download CSV"
              onClick={download}
              loading={isLoading}
            />
            {value.processed &&
              merchantKyb?.settlementReportEmails &&
              merchantKyb.settlementReportEmails.length > 0 && (
                <UtilityButton
                  text="Resend report email"
                  onClick={() => {
                    void sendReportEmail();
                  }}
                  loading={sendReportEmailLoading}
                />
              )}
            {value.processed && (
              <span css={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="subtitle" color="green">
                  Processed
                </Typography>
              </span>
            )}
            {!value.processed && (
              <UtilityButton
                text="Mark as processed"
                onClick={() => {
                  setShowMarkAsProcessedModal(true);
                }}
                loading={merchantKybLoading}
              />
            )}
          </div>
        </div>
      </Header>
      {refreshLoading && <QBLoader />}
      {
        <>
          <Card>
            <Typography variant="subtitle">{value.merchant.name}</Typography>
            <Typography>
              {value.startTime.toLocaleString()} -{' '}
              {value.endTime.toLocaleString()}
            </Typography>
          </Card>
          <div
            css={{
              display: 'flex',
              gap: spacing.s,
              marginTop: spacing.s,
            }}
          >
            <div css={{ flex: 1 }}>
              <Card>
                <Typography variant="subtitle">Amounts</Typography>
                <div
                  css={{
                    display: 'grid',
                    gap: spacing.xxs,
                    marginTop: spacing.xxs,
                  }}
                >
                  <Input
                    value={value.orderCount.toString()}
                    onChange={() => ({})}
                    label="Number of orders"
                    endAdornment="#"
                    disabled={value.processed}
                  />
                  <Input
                    value={value.totalAmount.toString()}
                    onChange={() => ({})}
                    label="Total Sales Volume"
                    endAdornment="€"
                    disabled={value.processed}
                  />
                  <Input
                    value={value.processingFee.toString()}
                    onChange={() => ({})}
                    label="Processing Fee"
                    endAdornment="€"
                    disabled={value.processed}
                  />
                  <Input
                    value={value.gatewayFee.toString()}
                    onChange={() => ({})}
                    label="Gateway Fee"
                    endAdornment="€"
                    disabled={value.processed}
                  />
                  <Input
                    value={value.settlementFee.toString()}
                    onChange={() => ({})}
                    label="Settlement Fee"
                    endAdornment="€"
                    disabled={value.processed}
                  />
                  <Input
                    value={value.settlementAmount.toString()}
                    onChange={() => ({})}
                    label="Net Settlement amount"
                    endAdornment="€"
                    disabled={value.processed}
                  />
                  <div
                    css={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
                  >
                    <div />
                    <Input
                      css={{}}
                      value={value.amountRemainingToBePaid.toFixed(2)}
                      onChange={() => ({})}
                      label="Amount remaining to be paid (including existing adjustments and payments)"
                      endAdornment="€"
                    />
                  </div>
                  <Adjustments
                    adjustments={value.adjustments}
                    disabled={value.processed}
                    onUpdateAdjustment={async (
                      updatedAdjustment: SettlementReportAdjustment
                    ) => {
                      await updateAdjustment(updatedAdjustment);
                      await refresh();
                    }}
                    onAddAdjustment={async () => {
                      await addNewAdjustment();
                      await refresh();
                    }}
                    onDeleteAdjustment={async (adjustmentId: string) => {
                      await deleteAdjustment(adjustmentId);
                      await refresh();
                    }}
                    deleteAdjustmentLoading={deleteLoading}
                    addAdjustementLoading={addLoading}
                    refreshLoading={refreshLoading}
                    maxRefundDebtAdjustment={value.maxRefundAmount}
                    refundDebtAdjustment={refundDebtAdjustment}
                    onUpdateRefundDebtAdjustment={async () => {
                      // if (
                      //   refundDebtAdjustment !== null &&
                      //   value.maxRefundAmount !== null &&
                      //   refundDebtAdjustment >= value.maxRefundAmount &&
                      //   refundDebtAdjustment < 0
                      // ) {
                      //   await updateRefundAdjustment(refundDebtAdjustment);
                      // }
                      if (refundDebtAdjustment !== null) {
                        await updateRefundAdjustment(refundDebtAdjustment);
                      }
                      await refresh();
                    }}
                    refundDebtAdjustmentSetter={setRefundDebtAdjustment}
                  />
                  <Payments
                    reportId={reportId}
                    merchantId={value.merchant.uuid}
                    refresh={refresh}
                    payments={value.payments}
                    disabled={value.processed}
                  />
                </div>
              </Card>
            </div>
            <AccountingReporting {...value.accounting} />
          </div>
        </>
      }
      {showDeleteModal &&
        (value.payments.length === 0 ? (
          <Modal
            title="Delete report"
            subtitle="Are you sure you want to delete this report?"
            closeModal={() => setShowDeleteModal(false)}
            primaryButtonProps={{
              text: 'Delete',
              onClick: async () => {
                await deleteReport();
                void navigate('/accounting/reports');
                setShowDeleteModal(false);
              },
              loading: deleteReportLoading,
              disabled: deleteReportLoading,
            }}
            secondaryButtonProps={{
              text: 'Cancel',
              onClick: () => setShowDeleteModal(false),
            }}
          />
        ) : (
          <Modal
            title="Delete report"
            subtitle="Reports cannot be deleted if they have payments on them."
            closeModal={() => setShowDeleteModal(false)}
            primaryButtonProps={{
              text: 'Close',
              onClick: () => setShowDeleteModal(false),
            }}
          />
        ))}
      {showMarkAsProcessedModal && (
        <Modal
          title="Mark as processed"
          subtitle="This will mark the report as processed and it will no longer be editable."
          closeModal={() => setShowMarkAsProcessedModal(false)}
          primaryButtonProps={{
            text: 'Mark as processed',
            onClick: markAsProcessed,
            loading: markProcessedLoading,
            disabled: markProcessedLoading || refreshLoading,
          }}
          secondaryButtonProps={{
            text: 'Cancel',
            onClick: () => setShowMarkAsProcessedModal(false),
          }}
        >
          {merchantKyb?.settlementReportEmails &&
            merchantKyb.settlementReportEmails.length > 0 && (
              <div
                css={{
                  display: 'flex',
                  gap: spacing.xxxs,
                  alignItems: 'center',
                }}
              >
                <Checkbox
                  checked={sendReportAsEmail}
                  onChange={setSendReportAsEmail}
                />
                <Typography variant="bodySmall" color={palette.grey.x500}>
                  Send PDF report to{' '}
                  <b>{merchantKyb.settlementReportEmails.toString()}</b>{' '}
                  automatically
                </Typography>
              </div>
            )}
        </Modal>
      )}
      <Snackbar
        variant="danger"
        message="Error in generating PDF, please try again later"
        open={pdfError}
        onClose={() => setPDFError(false)}
      />
      <Snackbar
        variant="success"
        message="Sent email report"
        open={!!sendReportEmailValue}
      />
    </>
  );
};
